import { Input, Select, Typography } from '@weareopenr/component-library';
import classnames from 'classnames/bind';
import { Control, Controller, FieldErrors, FieldValues, UseFormRegister, useWatch } from 'react-hook-form';
import { RoundingMethod, RoundingType } from 'src/types/price.types';
import RadioButtonList, { RadioButtonListOption } from '../RadioButtonList/RadioButtonList';
import styles from './RoundingRuleOptions.module.scss';

const cx = classnames.bind(styles);

interface IRoundingRuleOptionsProps {
  register: UseFormRegister<FieldValues>;
  control: Control<FieldValues>;
  errors?: FieldErrors<FieldValues>;
}

const roundingOption = (option: string) => ({ name: option, value: option });
const roundOptions = ['0.01', '0.02', '0.05', '0.10', '0.20', '0.25', '0.50'].map(roundingOption);

const roundingDirectionOptions: RadioButtonListOption[] = [
  {
    value: RoundingType.NONE,
    label: 'No Rounding',
    description: 'Prices are rounded to two decimal places using standard rounding.',
  },
  {
    value: RoundingType.UP,
    label: 'Round Up',
    description: 'Always round up to the next value.',
  },
  {
    value: RoundingType.DOWN,
    label: 'Round Down',
    description: 'Always round down to the previous value.',
  },
  {
    value: RoundingType.NEAREST,
    label: 'Round to Nearest',
    description: 'Always round to the nearest value.',
  },
];

const roundingMethodOptions: RadioButtonListOption[] = [
  {
    value: RoundingMethod.INTERVAL,
    label: 'Round to Intervals',
    description: 'Round to intervals of a value using the rounding direction',
  },
  {
    value: RoundingMethod.SPECIFIC,
    label: 'Round to Specific Value',
    description: 'Round to a specific decimal value using the rounding direction',
  },
];

export const RoundingRuleOptions = ({ register, control, errors }: IRoundingRuleOptionsProps) => {
  const [roundingType, roundingMethod] = useWatch({
    name: ['rounding.type', 'rounding.method'],
    control,
  });
  const noRounding = roundingType === RoundingType.NONE;

  return (
    <div className={cx('flex-column', 'container')}>
      <div className={cx('flex-column', 'section')}>
        <Typography size="large" weight="bold">
          Rounding Direction
        </Typography>

        <RadioButtonList name={'rounding.type'} options={roundingDirectionOptions} register={register} />
      </div>

      <div className={cx('flex-column', 'section')}>
        <Typography size="large" weight="bold" className={cx({ [styles.disabled]: noRounding })}>
          Rounding Method
        </Typography>

        <Typography className={cx({ [styles.disabled]: noRounding })}>
          Specify if you want to round up to the next interval value or to a specific value in pence.
        </Typography>

        <RadioButtonList
          name={'rounding.method'}
          options={roundingMethodOptions}
          register={register}
          disabled={noRounding}
        />
        <div className={styles['input-container']}>
          {roundingMethod === RoundingMethod.SPECIFIC && (
            <Input
              disabled={noRounding}
              type="number"
              step={0.01}
              placeholder="0.00"
              label="Rounding Threshold"
              error={errors?.['rounding']?.['value']?.message?.toString()}
              register={register('rounding.value', {
                valueAsNumber: true,
                required: {
                  value: !noRounding,
                  message: 'Please enter a rounding value',
                },
                min: {
                  value: 0,
                  message: 'Value must be at least 0.00',
                },
                max: {
                  value: 0.99,
                  message: 'Value must be no more than 0.99',
                },
              })}
            />
          )}

          {roundingMethod === RoundingMethod.INTERVAL && (
            <Controller
              name={'rounding.value'}
              control={control}
              rules={{
                required: {
                  value: !noRounding,
                  message: 'Please select a rounding value',
                },
              }}
              render={({ field: { name, onChange, value } }) => {
                let selectValue = value;
                if (noRounding) {
                  selectValue = '';
                } else if (value !== null && value !== undefined && value !== '') {
                  selectValue = Number(value).toFixed(2);
                }

                return (
                  <Select
                    name={name}
                    label={'Rounding Value'}
                    options={roundOptions}
                    value={selectValue}
                    onChange={onChange}
                    disabled={noRounding}
                    error={errors?.['rounding']?.['value']?.message?.toString()}
                    maxHeight={100}
                  />
                );
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default RoundingRuleOptions;
